import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';
import { FormTypes, FormulaType } from '~/pages/IncomeCalculator/enums';
import type { CalculationResult } from '~/pages/IncomeCalculator/types';

import calcLang from '../../ValidateCalculation/Calculators/language.json';
import BreakdownContainer from '../BreakdownContainer';
import lang from '../language.json';
import BreakdownValue from '../styles/BreakdownValue';
import BreakdownValueWithChild from '../styles/BreakdownValueWithChild';
import BreakdownYearContainer from '../styles/BreakdownYearContainer';
import SingleLineValue from '../styles/SingleLineValue';

const inputForm = FormTypes.Form1040;
const fhaFormula = new Set([
  FormulaType.InterestForm1040OneYearFHA,
  FormulaType.InterestForm1040YearOneFHA,
  FormulaType.InterestForm1040YearTwoFHA,
  FormulaType.DividendsInterestForm1040OneYearFHA,
  FormulaType.DividendsInterestForm1040YearOneFHA,
  FormulaType.DividendsInterestForm1040YearTwoFHA,
]);
const fields = calcLang.FIELDS.DIV_INT;

const Hr = (props) => (
  <Box {...props} role="separator" borderBottom="1px solid black" h={1} />
);

function buildAnnualValue(
  formula,
  taxExemptInterest,
  taxableInterest,
  dividends,
) {
  let grossUp;

  if (fhaFormula.has(formula.formulaId)) {
    grossUp = 1.15;
  } else {
    grossUp = 1.25;
  }

  const useInterest = formula.formulaName.includes('Interest');
  const useDividends = formula.formulaName.includes('Dividends');

  const total =
    (useInterest ? taxExemptInterest * grossUp + taxableInterest : 0) +
    (useDividends ? dividends : 0);

  const annualChildren = [];

  if (useInterest) {
    annualChildren.push(
      {
        name: `${fields.TAX_EXEMPT_INTEREST} * ` + grossUp.toString(),
        value: taxExemptInterest * grossUp,
      },
      {
        name: fields.TAXABLE_INTEREST,
        value: taxableInterest,
      },
    );
  }
  if (useDividends) {
    annualChildren.push({
      name: fields.ORDINARY_DIVIDENDS,
      value: dividends,
    });
  }
  const filteredAnnualChildren = annualChildren.reduce((acc, cur) => {
    if (cur.value <= 0) {
      return acc;
    }

    if (acc.length) {
      cur.sign = '+';
    }
    return [...acc, cur];
  }, []);

  return [filteredAnnualChildren, total];
}

type MonthlyIncomeBreakdownProps = Readonly<{
  selectedResult: CalculationResult;
  annuals: Record<number, number>;
}>;

function MonthlyIncomeBreakdown({
  selectedResult,
  annuals,
}: Readonly<MonthlyIncomeBreakdownProps>) {
  const finalFormula = selectedResult.formulas.find((formula) =>
    [FormulaType.OneYearMonthly, FormulaType.TwoYearMonthly].includes(
      formula.formulaId,
    ),
  )?.formulaId;
  const years = (Object.keys(annuals) as unknown as Array<number>)
    .sort((a, b) => b - a)
    .slice(0, finalFormula === FormulaType.OneYearMonthly ? 1 : 2);
  const total = Object.values(annuals).reduce((acc, value) => acc + value, 0);

  return (
    <Box mb={4}>
      <Text fontWeight="bold" fontSize={15} mb={2}>
        {lang.MONTHLY_CALC}
      </Text>
      <Box sx={{ display: 'inline-block' }}>
        {Object.values(years).map((year, i) => (
          <BreakdownValue
            value={annuals[year]}
            isChild={i > 0}
            isCurrency
            pr={4}
            key={year}
          />
        ))}
        <Hr my={1} />
      </Box>
      <Text fontSize="small">
        {years.length > 1 && <>{formatCurrency(total)} /</>} {years.length * 12}{' '}
        (months)
      </Text>
    </Box>
  );
}

type CalculationBreakdownProps = Readonly<{
  selectedResult: CalculationResult;
  onNewWindow?: (...args: any[]) => any;
  hideAccordion?: boolean;
}>;

type OneYearBreakdownProps = Readonly<{
  year: number;
}>;

export default function CalculationBreakdown({
  selectedResult,
  hideAccordion = false,
  onNewWindow,
}: CalculationBreakdownProps) {
  const fiscalYears = selectedResult.getFiscalYears().sort((a, b) => b - a);
  const formula = selectedResult.formulas[0];
  const annuals: Record<number, number> = {};

  function OneYearBreakdown({ year }: OneYearBreakdownProps) {
    const dividends = selectedResult.getInputAsFloat(
      inputForm,
      year,
      'ordinaryDividend',
    );
    const taxableInterest = selectedResult.getInputAsFloat(
      inputForm,
      year,
      'taxableInterest',
    );
    const taxExemptInterest = selectedResult.getInputAsFloat(
      inputForm,
      year,
      'taxExemptInterest',
    );
    const [annualChildren, annualTotal] = buildAnnualValue(
      formula,
      taxExemptInterest,
      taxableInterest,
      dividends,
    );
    annuals[year] = annualTotal;

    return (
      <BreakdownYearContainer title={year} flexGrow={1}>
        <BreakdownValueWithChild
          name={lang.ANNUAL_INCOME}
          value={annualTotal}
          isCurrency
          childrenData={annualChildren}
          fontWeight="bold"
        />
      </BreakdownYearContainer>
    );
  }

  return (
    <BreakdownContainer
      header={lang.HEADERS[selectedResult.calculatorTypeId]}
      hideAccordion={hideAccordion}
      onNewWindow={onNewWindow}
    >
      <Flex justifyContent="space-between" gridGap={4} mb={4}>
        {fiscalYears.map((fy) => (
          <OneYearBreakdown year={fy} key={fy} />
        ))}
      </Flex>
      <MonthlyIncomeBreakdown
        selectedResult={selectedResult}
        annuals={annuals}
      />
      <SingleLineValue
        title={lang.MONTHLY_INCOME}
        value={selectedResult.calculationAmount}
        isCurrency
      />
    </BreakdownContainer>
  );
}
